// Shared domain types + small pure helpers for the Factures module.
// Values here are pure so both the page and its extracted panels can use
// them without coupling to component state.

export interface IEntry {
	_id: string;
	type: 'mensual' | 'despesa' | 'ajust';
	unitat: string;
	schoolYear: string;
	amountCents: number;
	month?: string;
	concept?: string;
	expenseDate?: string;
	capName?: string;
	receipt?: { name: string; mime: string; size: number; path: string };
	unitatChargeCents?: number;
	reimbursedAmountCents?: number | null;
	reimbursements?: { at: string; deltaCents: number; by: string }[];
	note?: string;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
}

export interface IUnitatSummary {
	unitat: string;
	schoolYear: string;
	balanceCents: number;
	owed: { capName: string; owedCents: number }[];
	suggestedNextMensual: { month: string; amountCents: number } | null;
	entries: IEntry[];
	/** month → amountCents (0 = skipped month); from yearSummaryGet */
	mensuals?: Record<string, number>;
}

export interface ISummaryRow {
	balanceCents: number;
	owed: { capName: string; owedCents: number }[];
	mensuals?: Record<string, number>;
}

/** Inline popover state of the mensualitats grid. month = null means "the
 *  unitat's FIRST mensualiture — pick the starting month". */
export interface MensualQuickAdd {
	unitat: string;
	month: string | null;
	amount: string;
}

export const monthLabel = (ym: string): string => {
	if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return ym || '—';
	return new Intl.DateTimeFormat('ca-ES', {
		month: 'long',
		year: 'numeric',
	}).format(new Date(`${ym}-01T00:00:00`));
};

export const shortMonth = (ym: string): string =>
	new Intl.DateTimeFormat('ca-ES', { month: 'short' })
		.format(new Date(`${ym}-01T00:00:00`))
		.replace('.', '');

/** A unitat's mensualitats window is always this many months, starting at
 *  whatever month the treasurer registered first. */
export const MENSUAL_WINDOW = 8;

export const addMonthsYm = (ym: string, k: number): string => {
	const [y, m] = ym.split('-').map((x) => parseInt(x));
	const t = y * 12 + (m - 1) + k;
	return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
};

export const currentYm = (): string => {
	const n = new Date();
	return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
};

/** All 12 calendar months of a school year (YYYY-MM, chronological). */
export const schoolYearMonthsOf = (schoolYear: string): string[] => {
	const m = /^(\d{4})-(\d{2,4})/.exec(schoolYear);
	if (!m || !m[1]) return [];
	const y1 = m[1];
	const y2raw = m[2] || '';
	const y2full =
		y2raw.length === 4
			? y2raw
			: y2raw.length === 2
				? `${y1.slice(0, 2)}${y2raw}`
				: y1;
	return [
		...['09', '10', '11', '12'].map((mm) => `${y1}-${mm}`),
		...['01', '02', '03', '04', '05', '06', '07', '08'].map(
			(mm) => `${y2full}-${mm}`
		),
	];
};

export const fmtEUR = (cents: number): string => {
	const sign = cents < 0 ? '-' : '';
	return `${sign}${(Math.abs(cents) / 100)
		.toFixed(2)
		.replace('.', ',')} €`;
};

export const currentSchoolYear = (): string => {
	const now = new Date();
	// Season starts with the September of the school year.
	const y = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
	return `${y}-${y + 1}`;
};

/** '24,50' → 2450; null for anything not a decimal amount. */
export const moneyInputToCents = (v: string): number | null => {
	const m = v.trim().replace(',', '.');
	if (!/^-?\d+(\.\d{1,2})?$/.test(m)) return null;
	return Math.round(parseFloat(m) * 100);
};

/** Human label for an entry's date cell. */
export const entryDisplayDate = (e: IEntry): string => {
	if (e.type === 'mensual') return `Mensualitat ${e.month}`;
	if (e.type === 'despesa') {
		return e.expenseDate ? dateFmt(e.expenseDate) : dateFmt(e.createdAt);
	}
	return `Ajust · ${dateFmt(e.createdAt)}`;
};

/** Effective sort date: mensual month beats createdAt. */
export const dateOf = (e: IEntry): string =>
	e.type === 'mensual' ? `${e.month}-01` : (e.expenseDate ?? e.createdAt ?? '');

/** Signed amount on the ledger: despesas charge the unitat first. */
export const fmtEntryAmount = (e: IEntry): number =>
	e.type === 'despesa' ? -(e.unitatChargeCents ?? e.amountCents) : e.amountCents;

/** Money still owed to/from the cap for a despesa. */
export const outstanding = (e: IEntry): number =>
	e.amountCents - (e.reimbursedAmountCents ?? 0);

/** zip server filter mirrors the Estat select */
export const zipFilterFromEstat = (estat: string): string =>
	estat === 'tots' ? 'tots' : estat;

export const dateFmt = (iso?: string) =>
	iso
		? new Date(iso).toLocaleDateString('ca-ES', {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
			})
		: '';
